// TODO - Type 'any' needs to be fixed.
import { EditOutlined, FileDownloadOutlined,Print, SettingsSuggestOutlined } from "@mui/icons-material";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Skeleton from "@mui/material/Skeleton";
import { addDays, format, isPast } from "date-fns";
import { useMemo } from "react";
import React, { Dispatch, SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { SHIFTS, WORKERS} from "../../components/full-time-scheduler/constants";
import { openGenerateFullTimeShiftSchedulerModal } from "../../components/full-time-shifts/store/full-time-shifts-slice";
import { useDateFnsLocale } from "../../utils/date-locale";
import { hasPermission } from "../../utils/helpers";
import WeekNavigator from "../week-navigator";

interface FTCalendarToolbarProps {
	isFilterOpen: boolean;
	currentTab: string;
	setFilterOpen: Dispatch<SetStateAction<boolean>>;
	handleForward: () => void;
	handleBack: () => void;
	handleToday: () => void;
	handleCalendarClick: (e: any) => void;
	startingDay: Date;
	isLoading?: boolean;
	handleDemandClick?: () => void;
	resetDemandClick?:() => void;
  saveDemand?: () => void;
  disableSaveDemand?: boolean;
  setIsPrinting?: Dispatch<SetStateAction<boolean>>;
  handleExport?: () => void;
  isExporting?: boolean;
}

const FTCalendarToolbar: React.FC<FTCalendarToolbarProps> = ({
	isFilterOpen,
	currentTab,
	setFilterOpen, // From filter context
	handleForward,
	handleBack,
	handleToday,
	handleCalendarClick,
	startingDay,
	isLoading,
	handleDemandClick,
	resetDemandClick,
  saveDemand,
  disableSaveDemand,
  setIsPrinting,
  handleExport,
  isExporting,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const dateLocale = useDateFnsLocale();
	let currentDate = new Date().toISOString();
	// Get current date either from props or redux
	const workerSchedulercurrentDate = useSelector(
		(state: any) => state.workerScheduler?.currentDate
	);

	const fullTimeShiftsCurrentDate = useSelector(
		(state: any) => state.fullTimeShifts?.currentDate
	);

  const shiftDemandsCurrentDate = useSelector(
    (state: any) => state.shiftDemand?.currentDate
  );

	const isDemandWorkerOpen = useSelector(
		(state:any) => state.shiftDemand?.isShiftDemandOpen
	);

  const isPostDemandLoading = useSelector(
    (state: any) => state.shiftDemand?.postFTShiftDemands?.status === "pending"
  );

  const executionDetails = useSelector(
    (state: any) => state.fullTimeShifts?.executionDetails
  );

  const userRolesWithPermission = useSelector(
    (state: any) => state.user?.userData?.userRolesWithPermissions
  );

  const hasPermissionToGenerateFullTimeShiftSchedule = hasPermission(
    userRolesWithPermission, "FULL_TIME_SCHEDULER.GENERATE_SCHEDULE") && !isPast(startingDay);

  const hasPermissionToExport = hasPermission(
    userRolesWithPermission,
    "FULL_TIME_SCHEDULER.EXPORT_SCHEDULE"
  );

  const selectedLocationId = useSelector((state: any) => state.fullTimeShifts.selectedLocation);
  const scheduleEnabledLocations = useSelector((state: any) => state.user?.userData?.FTScheduleGenerationEnabledLocations);
  const isScheduleGenerationEnabled = scheduleEnabledLocations?.includes(selectedLocationId);

  // const isSchedulingDisabled = executionDetails?.isPublished;
  // const isPublishingDisabled = isSchedulingDisabled || !executionDetails?.isSaved;

  let scheduleTooltipTitle = null;

  // We are not showing the published tooltip for now, but keeping the code commented out for future reference.
  /*
  if (executionDetails?.isPublished && executionDetails?.lastExecutedOn) {
    const formattedDate = format(new Date(executionDetails.lastExecutedOn), "EEE, d MMM h:mm a");
    scheduleTooltipTitle = `Schedule is already published by ${executionDetails?.lastExecutedByName ?? "HR Manager"} on ${formattedDate}`;
  } else
   */
  if (executionDetails?.executionStatus === "InProgress" && executionDetails?.lastExecutedOn) {
    scheduleTooltipTitle = `Schedule execution is in progress by ${executionDetails?.lastExecutedByName ?? "HR Manager"} since ${format(new Date(executionDetails.lastExecutedOn), "EEE, d MMM h:mm a")}`;
  } else if (executionDetails?.lastExecutedOn && executionDetails?.executionStatus === "Completed") {
    const formattedDate = format(new Date(executionDetails.lastExecutedOn), "EEE, d MMM h:mm a");
    scheduleTooltipTitle = `Schedule was last executed by ${executionDetails?.lastExecutedByName ?? "HR Manager"} on ${formattedDate}`;
  }

	if (currentTab === SHIFTS) {
    isDemandWorkerOpen
      ? (currentDate = shiftDemandsCurrentDate || currentDate)
      : (currentDate = fullTimeShiftsCurrentDate || currentDate);
	} else {
		currentDate = workerSchedulercurrentDate || currentDate;
	}

	const isWeekView = useSelector((state: any) => state.supervisor?.weekView);

	const dateRangeText = useMemo(() => {
		const startDate = startingDay;
		const endDate = addDays(startDate, 6);
		return `${format(startDate, "MMMM d", { locale: dateLocale })} - ${format(
			endDate,
			"MMMM d, yyyy",
			{ locale: dateLocale }
		)}`;
	}, [startingDay, dateLocale]);

	return (
    <Box
      sx={{
        height: "56.5px",
        padding: "18px 16px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between">
        {isLoading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-start",
              flexDirection: "row",
            }}
          >
            {currentTab !== WORKERS ? (
              <>
                <Skeleton
                  variant="rounded"
                  width="32px"
                  height="32px"
                  sx={{
                    marginLeft: "12px",
                    borderRadius: "4px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={64}
                  height={32}
                  sx={{
                    borderRadius: "4px",
                    marginLeft: "24px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={32}
                  height={32}
                  sx={{
                    marginLeft: "24px",
                    borderRadius: "4px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={32}
                  height={32}
                  sx={{
                    borderRadius: "4px",
                    marginLeft: "24px",
                  }}
                />
                <Skeleton
                  variant="rounded"
                  width={192}
                  height={32}
                  sx={{
                    borderRadius: "4px",
                    marginLeft: "24px",
                  }}
                />
              </>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    marginLeft: "8px",
                  }}
                >
                  {!isDemandWorkerOpen && (
                    <Button
                      variant="outlined"
                      onClick={handleToday}
                      sx={{
                        height: "36px",
                        border: "1px solid rgba(0,0,0,0.08)",
                        borderColor: "rgba(0,0,0,0.08) !important",
                        color: "rgba(0,0,0,0.6)",
                      }}
                    >
                      {t("scheduler_today")}
                    </Button>
                  )}
                </Box>
                <WeekNavigator
                  handleBack={handleBack}
                  handleForward={handleForward}
                  handleCalendarClick={handleCalendarClick}
                  date={currentDate}
                  isWeekView={isWeekView}
                  dateRangeText={dateRangeText}
                />
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "10px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                // width: "282px",
                gap: "10px",
                marginLeft: "8px",
              }}
            >
              {!isDemandWorkerOpen && (
                <Button
                  variant="outlined"
                  onClick={handleToday}
                  sx={{
                    height: "36px",
                    border: "1px solid rgba(0,0,0,0.08)",
                    borderColor: "rgba(0,0,0,0.08) !important",
                    color: "rgba(0,0,0,0.6)",
                  }}
                >
                  {t("scheduler_today")}
                </Button>
              )}
            </Box>
            <WeekNavigator
              handleBack={handleBack}
              handleForward={handleForward}
              handleCalendarClick={handleCalendarClick}
              date={currentDate}
              isWeekView={isWeekView}
              dateRangeText={dateRangeText}
            />
          </Box>
        )}
      </Box>
      {currentTab === WORKERS && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "8px",
            marginRight: "8px",
          }}
        >
          {!isLoading && (
            <Button
              variant="text"
              sx={{
                fontFamily: "Roboto",
              }}
              onClick={() => {
                setIsPrinting?.(true);
                // Should add delay to ensure the print dialog opens after the state is set
                setTimeout(() => {
                  window.print();
                }, 100);
              }}
              startIcon={<Print />}
            >
              {t("common_print")}
            </Button>
          )}
          {hasPermissionToExport && (
            <Button
              variant="outlined"
              sx={{
                fontFamily: "Roboto",
              }}
              onClick={handleExport}
              disabled={isExporting || isLoading}
              startIcon={
                isExporting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileDownloadOutlined />
                )
              }
            >
              {t("common_export")}
            </Button>
          )}
        </Box>
      )}
      {currentTab === SHIFTS && (
        <>
          {!isDemandWorkerOpen ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
                marginRight: "8px",
              }}
            >
              {/*<Button*/}
              {/*  variant="text"*/}
              {/*  sx={{*/}
              {/*    fontFamily: "Roboto",*/}
              {/*  }}*/}
              {/*  onClick={() => {*/}
              {/*    // Handle the click for the "Add Shift" button*/}
              {/*  }}*/}
              {/*  startIcon={<FileUploadOutlinedIcon />}*/}
              {/*>*/}
              {/*  Print*/}
              {/*</Button>*/}
              <Button
                variant="text"
                sx={{
                  fontFamily: "Roboto",
                }}
                onClick={() => {
                  // Handle the click for the "Add Shift" button
                  handleDemandClick?.();
                }}
                startIcon={<EditOutlined />}
              >
                {t("full-time-scheduler_demand")}
              </Button>
              {hasPermissionToGenerateFullTimeShiftSchedule && 
              isScheduleGenerationEnabled &&
              (
                <Tooltip title={scheduleTooltipTitle}>
                  <span>
                    <Button
                      variant="outlined"
                      // disabled={isSchedulingDisabled}
                      sx={{
                        fontFamily: "Roboto",
                      }}
                      onClick={() => {
                        dispatch(openGenerateFullTimeShiftSchedulerModal());
                      }}
                      startIcon={<SettingsSuggestOutlined />}
                    >
                      Schedule
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "8px",
                marginRight: "8px",
              }}
            >
              <Button
                variant="outlined"
                sx={{
                  fontFamily: "Roboto",
                }}
                onClick={() => {
                  // Handle the click for the "Add Shift" button
                  resetDemandClick?.();
				
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                sx={{
                  fontFamily: "Roboto",
                }}
                disabled={disableSaveDemand}
                onClick={() => saveDemand?.()}
                loading = {isPostDemandLoading}
                loadingPosition="start"
              >
                {isPostDemandLoading ? "Saving..." : "Save Demand"}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default FTCalendarToolbar;
